import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService } from '@app/i18n/i18n.service';
import { FrankliHttpErrorResponse } from '@app/models/exception/frankli-http-error-response.model';
import { SessionEventType } from '@app/models/session-log/session-event-type.model';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { CalendarAPIService } from '@app/shared/api/calendar.api.service';
import { AuthAPIService } from '@app/shared/auth/auth.api.service';
import { Globals } from '@app/shared/globals/globals';
import { SessionLogService } from '@app/shared/session-log/session-log.service';
import { SSOProvider } from '@app/models/auth/sso-provider.enum';
import { SwalUtils } from 'app/shared/utils/swal.utils';
import { AuthMessages } from 'app/auth/auth.messages';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCode } from '@app/models/exception/error-codes.model';
import { firstValueFrom } from 'rxjs';
import { UserInit } from '@app/models/auth/user-init.model';
import { parseSsoState, SSO_RECONNECT_RETURN_URL, takeCalendarConnectCheck } from '@app/shared/auth/sso-authorize';

@Component({
  selector: 'app-callback',
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit {

  constructor(
    private route: ActivatedRoute,
    private authService: AuthAPIService,
    private router: Router,
    public globals: Globals,
    private sessionLogService: SessionLogService,
    private i18nService: I18nService,
    private userAPIService: UserAPIService,
    private swalUtils: SwalUtils,
    private calendarAPIService: CalendarAPIService
  ) { }

  // What to tell the user when the backend refuses a calendar connect; anything else gets the generic failure.
  private static readonly CONNECT_REFUSALS: Partial<Record<ErrorCode, string>> = {
    [ErrorCode.CALENDAR_ACCOUNT_MISMATCH]: AuthMessages.CALENDAR_CONNECT_WRONG_ACCOUNT,
    [ErrorCode.CALENDAR_ALREADY_LINKED]: AuthMessages.CALENDAR_CONNECT_ALREADY_LINKED,
    [ErrorCode.CALENDAR_CONNECT_NOT_AVAILABLE]: AuthMessages.CALENDAR_CONNECT_NOT_AVAILABLE
  };

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code');
    const stateParam = this.route.snapshot.queryParamMap.get('state');

    // Read before anything else: a Reconnect or a calendar connect comes back here even when it was cancelled at the
    // provider (no code).
    const { provider, reconnect, reconnectUserId, connect, connectUserId, connectCheck } = parseSsoState(stateParam || '');
    if (reconnect) {
      this.reconnectCalendar(code, provider, reconnectUserId);
      return;
    }
    if (connect) {
      this.connectCalendar(code, provider, connectUserId, connectCheck);
      return;
    }

    if (!code || !stateParam) {
      this.handleError('Something went wrong when trying to sign you in.');
      return;
    }

    if (provider === undefined) {
      this.handleError('Something went wrong when trying to sign you in.');
      return;
    }

    firstValueFrom(
      this.authService.authenticateSSO({ authorizationCode: code, provider })
    )
      .then(userInit => this.completeSignIn(userInit, provider, '/'))
      .catch((error: FrankliHttpErrorResponse) => {
        if (error.status === 410) {
          this.swalUtils.displayWarning({
            title: AuthMessages.SSO_GOOGLE_INVALIDATED,
            text: AuthMessages.SSO_GOOGLE_INVALIDATED_DESCRIPTION,
            confirmButtonText: AuthMessages.RETURN_TO_LOGIN
          }).then(() => {
            this.router.navigateByUrl('/');
          });
          return;
        } else if (error.error) {
          switch (error.error.errorCode) {
            case ErrorCode.LOGIN_ACCOUNT_CONNECTED_TO_GOOGLE:
              this.swalUtils.displayWarning({
                text: error.error.message,
                confirmButtonText: AuthMessages.RETURN_TO_LOGIN
              }).then(() => {
                this.router.navigateByUrl('/');
              });
              return;
            case ErrorCode.LOGIN_ACCOUNT_CONNECTED_TO_MICROSOFT:
              this.swalUtils.displayWarning({
                text: error.error.message,
                confirmButtonText: AuthMessages.RETURN_TO_LOGIN
              }).then(() => {
                this.router.navigateByUrl('/');
              });
              return;
            case ErrorCode.LOGIN_REFRESH_TOKEN_EXPIRED_MICROSOFT:
              localStorage.setItem('refresh_microsoft_token', 'true');
              break;
          }
        }

        const errorMessage =
          typeof error.error === 'string'
            ? error.error
            : error.error?.message || 'Something went wrong when trying to sign you in.';

        this.handleError(errorMessage);
      });
  }

  // Stores the new session and lands on landingUrl: every successful SSO sign-in finishes the same way.
  private completeSignIn(userInit: UserInit, provider: SSOProvider, landingUrl: string) {
    console.info('Callback: ', userInit);
    localStorage.setItem('access_token', userInit.accessToken);
    localStorage.removeItem('refresh_microsoft_token');
    this.globals.init(userInit);
    this.globals.ssoProvider = provider;
    this.globals.welcomeViewed = false;
    this.i18nService.language = this.globals.user.configuration.preferredLocale;
    this.sessionLogService.log(SessionEventType.SESSION_START_LOGIN);

    this.userAPIService.setTimezone(this.globals.user).subscribe(user => {
      this.globals.updateUser(user);
      this.router.navigateByUrl(landingUrl);
    });
  }

  // A calendar Reconnect started on the Notifications page by the user in the state. The user is already signed in, so
  // every outcome returns to that page, never to the login page, and nothing is left behind in sso_error.
  private reconnectCalendar(code: string | null, provider: SSOProvider | undefined, expectedUserId: number | null) {
    if (!code) {
      // Cancelled at the provider (or access denied there): back to where they were, without an error for a choice
      // they made; any other provider error is shown.
      if (this.route.snapshot.queryParamMap.get('error') === 'access_denied') {
        this.router.navigateByUrl(SSO_RECONNECT_RETURN_URL);
      } else {
        this.showReconnectError(AuthMessages.SSO_RECONNECT_FAILED);
      }
      return;
    }
    // Without the starting user the backend can't refuse a sign-in as someone else, so don't sign in at all.
    if (provider === undefined || expectedUserId === null) {
      this.showReconnectError(AuthMessages.SSO_RECONNECT_FAILED);
      return;
    }
    // The state arrives in a link anyone can build, so only the signed-in user's own Reconnect goes ahead. Otherwise a
    // link carrying someone else's code and id would sign this browser in as them.
    if (expectedUserId !== this.globals.user?.id) {
      this.showReconnectError(AuthMessages.SSO_RECONNECT_FAILED);
      return;
    }

    firstValueFrom(this.authService.authenticateSSO({ authorizationCode: code, provider, expectedUserId }))
      .then(userInit => {
        // The backend already refuses a sign-in as someone else before saving any token; this keeps the session from
        // ever switching to them should that check be bypassed.
        if (userInit.user?.id !== expectedUserId) {
          this.showReconnectError(AuthMessages.SSO_RECONNECT_WRONG_ACCOUNT);
          return;
        }
        this.completeSignIn(userInit, provider, SSO_RECONNECT_RETURN_URL);
      })
      .catch((error: FrankliHttpErrorResponse) => {
        if (error?.error?.errorCode === ErrorCode.SSO_ACCOUNT_MISMATCH) {
          this.showReconnectError(AuthMessages.SSO_RECONNECT_WRONG_ACCOUNT);
          return;
        }
        this.handleReconnectError(error);
      });
  }

  // A calendar connect started on the Notifications page by the user in the state. Unlike a Reconnect it never signs in:
  // the code goes to the calendar connect endpoint, so the user stays signed in as themselves, and every outcome
  // returns to the Notifications page.
  private connectCalendar(code: string | null, provider: SSOProvider | undefined, startedBy: number | null, check: string | null) {
    // Read (and so used up) whatever happens next, so a value is good for one callback only.
    const expectedCheck = takeCalendarConnectCheck();
    if (!code) {
      // Cancelled at the provider (or access denied there): back to where they were, without an error for a choice
      // they made; any other provider error is shown.
      if (this.route.snapshot.queryParamMap.get('error') === 'access_denied') {
        this.router.navigateByUrl(SSO_RECONNECT_RETURN_URL);
      } else {
        this.showReconnectError(AuthMessages.CALENDAR_CONNECT_FAILED);
      }
      return;
    }
    // The state arrives in a link anyone can build, so only a connect this tab started goes ahead: its random value
    // must match the one kept when it started, and it must be the signed-in user's. Otherwise a link carrying someone
    // else's code could store their calendar on this user.
    if (provider === undefined || startedBy === null || startedBy !== this.globals.user?.id
        || !check || check !== expectedCheck) {
      this.showReconnectError(AuthMessages.CALENDAR_CONNECT_FAILED);
      return;
    }

    firstValueFrom(this.calendarAPIService.connect(provider, code))
      .then(() => this.router.navigateByUrl(SSO_RECONNECT_RETURN_URL))
      .catch((error: FrankliHttpErrorResponse) => {
        const refusal = CallbackComponent.CONNECT_REFUSALS[error?.error?.errorCode as ErrorCode];
        this.showReconnectError(refusal || AuthMessages.CALENDAR_CONNECT_FAILED);
      });
  }

  // A failed Reconnect: the user is still signed in, so the login page (where handleError's message is shown) isn't
  // where they are. Show the reason and take them back to the Notifications page they started from.
  private handleReconnectError(error: FrankliHttpErrorResponse) {
    const message =
      typeof error?.error === 'string'
        ? error.error
        : error?.error?.message || AuthMessages.SSO_RECONNECT_FAILED;
    this.showReconnectError(message);
  }

  private showReconnectError(message: string) {
    this.swalUtils.displayErrorSwal({ text: message }).then(() => {
      this.router.navigateByUrl(SSO_RECONNECT_RETURN_URL);
    });
  }

  private handleError(message: string) {
    localStorage.setItem('sso_error', `${Date.now()}: ${message}`);
    this.router.navigateByUrl('/');
  }

}
